(function () {
  class EbCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-eb-carousel-track]');
      this.prev = this.querySelector('[data-eb-carousel-prev]');
      this.next = this.querySelector('[data-eb-carousel-next]');
      this.prev?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));
      this.track?.addEventListener('scroll', () => this.update(), { passive: true });
      this.update();
    }

    scroll(direction) {
      const amount = this.track?.clientWidth || 320;
      this.track?.scrollBy({ left: direction * amount * 0.8, behavior: 'smooth' });
    }

    update() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }

  class EbFeaturedProduct extends HTMLElement {
    connectedCallback() {
      this.panels = [...this.querySelectorAll('[data-eb-fp-panel]')];
      this.tabs = [...this.querySelectorAll('[data-eb-fp-tab]')];
      this.modelThumbs = [...this.querySelectorAll('[data-eb-fp-thumb-model]')];

      // Model tab switching
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.switchModel(tab.dataset.ebFpTab));
      });
      this.modelThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.switchModel(thumb.dataset.ebFpThumbModel));
      });

      // Bind interactions for whichever panel is active on load
      const activePanel = this.panels.find((panel) => !panel.hidden) || this.panels[0];
      if (activePanel) this.bindPanel(activePanel);
    }

    /* ---- Model switching ---- */
    switchModel(panelId) {
      const target = this.panels.find((panel) => panel.dataset.ebFpPanel === panelId);
      if (!target || target === this.activePanel) return;

      this.panels.forEach((panel) => {
        const isTarget = panel === target;
        panel.hidden = !isTarget;
        if (isTarget) {
          panel.removeAttribute('aria-hidden');
        } else {
          panel.setAttribute('aria-hidden', 'true');
          panel.classList.remove('is-switching');
        }
      });

      this.tabs.forEach((tab) => {
        const on = tab.dataset.ebFpTab === panelId;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.modelThumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.ebFpThumbModel === panelId);
      });

      // Re-trigger entrance animation
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        target.classList.remove('is-switching');
        // Force reflow so the animation replays
        void target.offsetWidth;
        target.classList.add('is-switching');
        target.addEventListener(
          'animationend',
          () => target.classList.remove('is-switching'),
          { once: true }
        );
      }

      this.bindPanel(target);
    }

    /* ---- Scope all variant/thumb/cart handlers to one panel ---- */
    bindPanel(panel) {
      this.activePanel = panel;
      this.product = JSON.parse(panel.querySelector('[data-eb-featured-product-json]')?.textContent || '{}');
      this.select = panel.querySelector('[data-eb-featured-variant]');
      this.idInput = panel.querySelector('[data-eb-featured-id]');
      this.price = panel.querySelector('[data-eb-featured-price]');
      this.currentPrice = panel.querySelector('[data-eb-featured-current]');
      this.comparePrice = panel.querySelector('[data-eb-featured-compare]');
      this.button = panel.querySelector('[data-eb-featured-atc]');
      this.status = panel.querySelector('[data-eb-featured-status]');
      this.image = panel.querySelector('[data-eb-featured-image]');
      this.thumbs = [...panel.querySelectorAll('[data-eb-featured-thumb]')];
      this.swatches = [...panel.querySelectorAll('[data-eb-featured-swatch]')];
      this.form = panel.querySelector('[data-eb-featured-form]');

      if (panel.dataset.ebFpBound === '1') return;
      panel.dataset.ebFpBound = '1';

      this.select?.addEventListener('change', () => {
        this.bindPanel(panel);
        this.updateVariant();
      });
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          this.bindPanel(panel);
          if (this.select) {
            this.select.value = swatch.dataset.ebFeaturedSwatch;
          }
          this.updateVariant();
        });
      });
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.bindPanel(panel);
          this.setActiveThumb(thumb);
          this.updateImage(thumb.dataset.ebFeaturedThumb);
        });
      });
      this.form?.addEventListener('submit', (event) => {
        this.bindPanel(panel);
        this.submit(event);
      });
    }

    updateVariant() {
      const panel = this.activePanel;
      if (panel) {
        this.select = panel.querySelector('[data-eb-featured-variant]');
        this.idInput = panel.querySelector('[data-eb-featured-id]');
        this.price = panel.querySelector('[data-eb-featured-price]');
        this.currentPrice = panel.querySelector('[data-eb-featured-current]');
        this.comparePrice = panel.querySelector('[data-eb-featured-compare]');
        this.button = panel.querySelector('[data-eb-featured-atc]');
        this.status = panel.querySelector('[data-eb-featured-status]');
        this.image = panel.querySelector('[data-eb-featured-image]');
        this.thumbs = [...panel.querySelectorAll('[data-eb-featured-thumb]')];
        this.swatches = [...panel.querySelectorAll('[data-eb-featured-swatch]')];
      }
      const id = Number(this.select?.value || this.idInput?.value);
      let variant = this.product.variants?.find((item) => Number(item.id) === id);
      if (!variant) {
        const swatch = this.swatches?.find((item) => Number(item.dataset.ebFeaturedSwatch) === id);
        if (swatch) {
          variant = {
            id,
            price: Number(swatch.dataset.ebFeaturedSwatchPrice || 0),
            compare_at_price: Number(swatch.dataset.ebFeaturedSwatchCompare || 0),
            available: swatch.dataset.ebFeaturedSwatchAvailable === 'true',
            featured_image: swatch.dataset.ebFeaturedSwatchImage ? { src: swatch.dataset.ebFeaturedSwatchImage } : null,
          };
        }
      }
      if (!variant) return;
      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.setAttribute('value', String(variant.id));
      }
      if (this.currentPrice) this.currentPrice.textContent = this.money(variant.price);
      if (this.comparePrice) {
        const comparePrice = Number(variant.compare_at_price || 0);
        const price = Number(variant.price || 0);
        this.comparePrice.textContent = comparePrice > price ? this.money(comparePrice) : '';
        this.comparePrice.hidden = comparePrice <= price;
      } else if (this.price) {
        this.price.textContent = this.money(variant.price);
      }
      if (this.button) {
        this.button.disabled = !variant.available;
        this.button.classList.remove('is-added');
        this.button.textContent = variant.available ? this.dataset.addLabel : this.dataset.soldLabel;
      }
      if (this.status) this.status.textContent = '';
      if (variant.featured_image?.src) {
        this.updateImage(variant.featured_image.src);
        const matchingThumb = this.thumbs.find((thumb) => {
          const thumbUrl = thumb.dataset.ebFeaturedThumb || '';
          return thumbUrl.includes(variant.featured_image.id) || thumbUrl.includes(variant.featured_image.src);
        });
        if (matchingThumb) this.setActiveThumb(matchingThumb);
      }
      this.setActiveSwatch(variant.id);
    }

    updateImage(src) {
      if (!this.image || !src) return;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.image.classList.remove('is-switching');
        void this.image.offsetWidth;
        this.image.classList.add('is-switching');
        this.image.addEventListener(
          'animationend',
          () => this.image?.classList.remove('is-switching'),
          { once: true }
        );
      }
      this.image.removeAttribute('srcset');
      this.image.removeAttribute('sizes');
      this.image.src = src;
    }

    setActiveThumb(activeThumb) {
      this.thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb === activeThumb));
    }

    setActiveSwatch(variantId) {
      this.swatches?.forEach((swatch) => {
        swatch.classList.toggle('is-active', Number(swatch.dataset.ebFeaturedSwatch) === Number(variantId));
      });
    }

    submit(event) {
      if (!window.fetch) return;
      event.preventDefault();
      const form = event.currentTarget;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      })
        .then((response) => {
          if (!response.ok) throw response;
          return response.json();
        })
        .then((item) => {
          if (this.button) {
            this.button.classList.add('is-added');
            this.button.textContent = this.dataset.addedLabel || this.dataset.addLabel;
            window.clearTimeout(this.addedTimer);
            this.addedTimer = window.setTimeout(() => {
              this.button?.classList.remove('is-added');
              if (this.button && !this.button.disabled) this.button.textContent = this.dataset.addLabel;
            }, 2200);
          }
          if (this.status) this.status.textContent = this.dataset.addedLabel || '';
          this.dispatchEvent(new CustomEvent('eb:cart:add', { bubbles: true, composed: true, detail: { lines: [{ id: Number(this.idInput?.value || item.id), quantity: 1 }], item } }));
        })
        .catch(() => {
          if (this.status) this.status.textContent = this.dataset.errorLabel || '';
        });
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(amount);
      } catch (error) {
        return amount.toFixed(2);
      }
    }
  }

  if (!customElements.get('eb-carousel')) customElements.define('eb-carousel', EbCarousel);
  if (!customElements.get('eb-featured-product')) customElements.define('eb-featured-product', EbFeaturedProduct);
})();
